/**
 * Ohio Home Health Agency State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: OAC Chapter 3701-60 (Home Health Agencies)
 *   Effective January 27, 2023 - implements ORC Chapter 3740
 * - Statutory Authority: ORC Chapter 3740 (Home Health and Nonmedical Home Services)
 * - Background Checks: ORC 109.572, OAC 3701-60-05 through 3701-60-09
 * - STNA Certification: OAC 3701-17, 3701-19
 * - Medicare Conditions of Participation: 42 CFR Part 484 (for Medicare-certified agencies)
 * 
 * REGULATORY BODY:
 * Ohio Department of Health (ODH)
 * Bureau of Community Health Care Facilities and Services
 * Phone: (614) 466-3543
 * Website: https://odh.ohio.gov/know-our-programs/home-health-care
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - License term: 3 years per OAC 3701-60-03(J)
 * - License fee: $250 per OAC 3701-60-03(A)(2) and (B)(2)
 * - Two license types: Skilled Home Health Services, Nonmedical Home Health Services
 * - Change notification: 10 days per OAC 3701-60-03(I)
 * - Renewal application: 90 days before expiration per OAC 3701-60-03(H)
 * - Criminal records check: Every 5 years per OAC 3701-60-06(B)
 * - Database review: 6 databases per OAC 3701-60-05(A)
 * - Conditional employment: Allowed per OAC 3701-60-07
 * 
 * MEDICARE CERTIFICATION REQUIREMENTS (42 CFR 484):
 * - Initial HHA training: 75 hours per 42 CFR 484.80(b)
 * - Annual in-service: 12 hours per 42 CFR 484.80(d)
 * - Clinical record retention: 5 years per 42 CFR 484.110(c)(1)
 * - RN supervision of HHA: Every 14 days per 42 CFR 484.80(h)
 * 
 * NOTE: OAC 3701-60 replaced former OAC 3701-56 effective October 1, 2022
 * 
 * Last Verified: December 2024 against current OAC 3701-60 and 42 CFR 484
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const OHIO_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'OH-A1',
    itemId: 'A1',
    stateCode: 'OH',
    title: 'Home Health Agency License Status',
    description: 'Ohio requires all home health agencies to be licensed by ODH under OAC 3701-60',
    severity: 'high',
    regulationCode: '3701-60-02',
    regulationName: 'License',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov/know-our-programs/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from ODH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per OAC 3701-60-03(J): License valid for 3 years. Operating without license is a misdemeanor per 3701-60-02(E).',
      },
      {
        id: 'q2',
        question: 'Which type of license do you hold?',
        inputType: 'select',
        options: [
          { value: 'skilled', label: 'Skilled Home Health Services License' },
          { value: 'nonmedical', label: 'Nonmedical Home Health Services License' },
          { value: 'both', label: 'Both licenses' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 3701-60-03(C): Skilled license holders may provide nonmedical services without additional license.',
      },
      {
        id: 'q3',
        question: 'What is your home health agency license expiration date?',
        inputType: 'date',
        helpText: 'License valid for 3 years per 3701-60-03(J). Submit renewal 90 days before expiration per 3701-60-03(H).',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 180, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q3', value: 90, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your ODH Home Health Agency license is current per OAC 3701-60.',
      atRisk: 'Your license expires within 180 days. Submit renewal application 90 days before expiration per 3701-60-03(H).',
      nonCompliant: 'Operating without a valid license violates OAC 3701-60-02 and is a misdemeanor.',
      unknown: 'Verify your license status with ODH at (614) 466-3543.',
    },
    stateNotes: [
      'License valid for 3 years per 3701-60-03(J)',
      'License fee: $250 per 3701-60-03(A)(2) and (B)(2)',
      'Two license types: Skilled and Nonmedical',
      'Skilled license allows nonmedical services per 3701-60-03(C)',
      'Out-of-state agencies must establish Ohio administrative office per 3701-60-03(D)',
    ],
  },

  {
    id: 'OH-A2',
    itemId: 'A2',
    stateCode: 'OH',
    title: 'License Type and Service Scope',
    description: 'Ohio has two distinct license types with different service scopes per OAC 3701-60-01',
    severity: 'high',
    regulationCode: '3701-60-01',
    regulationName: 'Services',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov/know-our-programs/home-health-care',
    questions: [
      {
        id: 'q1',
        question: 'Which services does your agency provide?',
        inputType: 'checklist',
        options: [
          { value: 'skilled_nursing', label: 'Skilled nursing care' },
          { value: 'pt', label: 'Physical therapy' },
          { value: 'ot', label: 'Occupational therapy' },
          { value: 'st', label: 'Speech-language pathology' },
          { value: 'msw', label: 'Medical social services' },
          { value: 'hha', label: 'Home health aide services' },
          { value: 'personal_care', label: 'Personal care services' },
        ],
        required: true,
        helpText: 'Per 3701-60-01: Skilled services (J)(1-5) require Skilled license. HHA/personal care (K), (V) require at minimum Nonmedical license.',
      },
      {
        id: 'q2',
        question: 'Do you have the appropriate license type for all services you provide?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - license covers all services' },
          { value: 'no', label: 'No - providing services outside license scope' },
          { value: 'unsure', label: 'Not sure' },
        ],
        helpText: 'Skilled services require Skilled license. Skilled license holders may provide nonmedical services.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency services align with license type.',
      atRisk: 'Verify your license type covers all services provided.',
      nonCompliant: 'Providing services outside license scope violates OAC 3701-60-02.',
      unknown: 'Review service scope against license type.',
    },
    stateNotes: [
      'Skilled Home Health Services: Nursing, PT, OT, ST, Medical Social Services per 3701-60-01(Z)',
      'Nonmedical Home Health Services: HHA services and personal care per 3701-60-01(R)',
      'Home health aide services defined at 3701-60-01(K)',
      'Personal care services defined at 3701-60-01(V)',
    ],
  },

  {
    id: 'OH-A3',
    itemId: 'A3',
    stateCode: 'OH',
    title: 'Change Notification Compliance',
    description: 'Ohio requires 10-day notification of changes per OAC 3701-60-03(I)',
    severity: 'medium',
    regulationCode: '3701-60-03(I)',
    regulationName: 'Notification',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is your information current with ODH (ownership, administrator, operating location)?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, all information is current' },
          { value: 'pending', label: 'Changes pending notification to ODH' },
          { value: 'outdated', label: 'No, information is outdated' },
          { value: 'unsure', label: 'Not sure if current' },
        ],
        required: true,
        helpText: 'Per 3701-60-03(I): Written notice to ODH required within 10 days of any change.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'current', result: 'COMPLIANT' },
      { condition: 'includes', value: ['pending', 'unsure'], result: 'AT_RISK' },
      { condition: 'equals', value: 'outdated', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency information is current with ODH.',
      atRisk: 'Notify ODH within 10 days of any changes per 3701-60-03(I).',
      nonCompliant: 'Failure to notify ODH of changes violates 3701-60-03(I).',
      unknown: 'Verify your agency information is current with ODH.',
    },
    stateNotes: [
      'Written notice within 10 days for any change per 3701-60-03(I)(1)',
      'Includes ownership, controlling interest, officers, statutory agent',
      'Includes operating location, services provided, geographic area',
      'Criminal records check policy changes must also be reported',
    ],
  },

  {
    id: 'OH-A4',
    itemId: 'A4',
    stateCode: 'OH',
    title: 'Medicare Certification/Accreditation Status',
    description: 'Ohio recognizes Medicare certification and national accreditation for skilled services',
    severity: 'medium',
    regulationCode: '3701-60-03(A)(3)',
    regulationName: 'Certification',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'What is your Medicare/accreditation status?',
        inputType: 'select',
        options: [
          { value: 'medicare', label: 'Medicare-certified' },
          { value: 'achc', label: 'Accredited by ACHC' },
          { value: 'chap', label: 'Accredited by CHAP' },
          { value: 'joint_commission', label: 'Accredited by Joint Commission' },
          { value: 'oda', label: 'ODA-certified for community-based long-term care' },
          { value: 'attestation', label: 'Attestation only (meets Medicare CoP but not certified)' },
          { value: 'none', label: 'None of the above' },
        ],
        required: true,
        helpText: 'Per 3701-60-03(A)(3): Skilled license requires certification, accreditation, or attestation.',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['medicare', 'achc', 'chap', 'joint_commission', 'oda'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'attestation', result: 'AT_RISK' },
      { condition: 'equals', value: 'none', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification or accreditation documented.',
      atRisk: 'Attestation-only agencies should pursue formal certification or accreditation.',
      nonCompliant: 'Skilled home health services require certification, accreditation, or attestation per 3701-60-03(A)(3).',
      unknown: 'Verify certification/accreditation status.',
    },
    stateNotes: [
      'Medicare certification per 3701-60-03(A)(3)(a)',
      'Recognized accreditors: ACHC, CHAP, Joint Commission per 3701-60-03(A)(3)(b)',
      'ODA certification per 3701-60-03(A)(3)(c)',
      'Attestation allowed per 3701-60-03(A)(3)(d)',
      'New agencies (after 9/30/2021) need $50,000 surety bond per 3701-60-03(A)(3)(e)(ii)',
    ],
  },

  {
    id: 'OH-A5',
    itemId: 'A5',
    stateCode: 'OH',
    title: 'Surety Bond Requirement (New Agencies)',
    description: 'New agencies starting after 9/30/2021 require surety bond per OAC 3701-60-03',
    severity: 'high',
    regulationCode: '3701-60-03(A)(3)(e)',
    regulationName: 'Surety Bond',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Was your agency providing home health services before September 30, 2021?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - operating before 9/30/2021' },
          { value: 'no', label: 'No - started after 9/30/2021' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have the required surety bond?',
        inputType: 'select',
        options: [
          { value: 'skilled_50k', label: 'Yes - $50,000 bond for skilled services' },
          { value: 'nonmedical_20k', label: 'Yes - $20,000 bond for nonmedical services' },
          { value: 'no', label: 'No surety bond' },
          { value: 'na', label: 'N/A - exempt (pre-9/30/2021 agency)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'no',
        },
        helpText: 'Skilled: $50,000 per 3701-60-03(A)(3)(e)(ii). Nonmedical: $20,000 per 3701-60-03(B)(3)(k)(ii).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['skilled_50k', 'nonmedical_20k', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Surety bond requirement met or exempt.',
      atRisk: 'Verify surety bond status.',
      nonCompliant: 'New agencies (after 9/30/2021) require surety bond.',
      unknown: 'Verify surety bond requirement.',
    },
    stateNotes: [
      'Skilled services: $50,000 bond per 3701-60-03(A)(3)(e)(ii)',
      'Nonmedical services: $20,000 bond per 3701-60-03(B)(3)(k)(ii)',
      'Bond must be from company licensed in Ohio',
      'Agencies operating before 9/30/2021 are exempt',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING
  // ============================================
  {
    id: 'OH-B1',
    itemId: 'B1',
    stateCode: 'OH',
    title: 'Database Review Requirements',
    description: 'Ohio requires review of 6 databases before criminal records check per OAC 3701-60-05',
    severity: 'high',
    regulationCode: '3701-60-05(A)',
    regulationName: 'Database Review',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which databases do you check for ALL applicants/employees providing direct care?',
        inputType: 'checklist',
        options: [
          { value: 'sam', label: 'System for Award Management (SAM.gov) - 3701-60-05(A)(1)' },
          { value: 'oig', label: 'OIG Exclusion List (exclusions.oig.hhs.gov) - 3701-60-05(A)(2)' },
          { value: 'dodd', label: 'DODD Abuse Registry - 3701-60-05(A)(3)' },
          { value: 'sex_offender', label: 'Sex Offender Registry (AG) - 3701-60-05(A)(4)' },
          { value: 'odrc', label: 'ODRC Inmate Database - 3701-60-05(A)(5)' },
          { value: 'nurse_aide', label: 'Ohio Nurse Aide Registry - 3701-60-05(A)(6)' },
        ],
        required: true,
        helpText: 'Per 3701-60-05(A): ALL 6 databases must be checked BEFORE conducting criminal records check.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All 6 required database checks are being performed per 3701-60-05(A).',
      atRisk: 'Some required database checks may be missing.',
      nonCompliant: 'Multiple required database checks are missing. All 6 are mandatory.',
      unknown: 'Verify database review procedures against 3701-60-05(A).',
    },
    stateNotes: [
      'All 6 databases required per 3701-60-05(A)',
      'Must be checked BEFORE criminal records check',
      'Disqualifying info bars employment per 3701-60-05(B)',
      'For out-of-state residents: Also check nurse aide registry in prior states per 3701-60-05(A)(6)',
    ],
  },

  {
    id: 'OH-B2',
    itemId: 'B2',
    stateCode: 'OH',
    title: 'Criminal Records Check Requirements',
    description: 'Ohio requires BCI&I and FBI background checks per OAC 3701-60-06',
    severity: 'high',
    regulationCode: '3701-60-06',
    regulationName: 'Criminal Records Check Requirements',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct criminal records checks through BCI&I for all direct care applicants?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 3701-60-06(A): Chief administrator must request criminal records check for all direct care applicants.',
      },
      {
        id: 'q2',
        question: 'For applicants who have NOT been Ohio residents for 5 years, do you also request FBI fingerprint check?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - FBI check included for non-5-year residents' },
          { value: 'no', label: 'No - only state check' },
          { value: 'all', label: 'Yes - FBI check for all applicants regardless of residency' },
        ],
        helpText: 'Per 3701-60-06(C): FBI check required if not Ohio resident for 5 years.',
      },
      {
        id: 'q3',
        question: 'Do you conduct criminal records checks for current employees every 5 years?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 3701-60-06(B): Background check required every 5 years for current employees.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'includes', questionId: 'q2', value: ['yes', 'all'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Criminal records check procedures meet 3701-60-06 requirements.',
      atRisk: 'Criminal records check procedures may be incomplete.',
      nonCompliant: 'Criminal records checks not being conducted as required.',
      unknown: 'Verify criminal records check procedures.',
    },
    stateNotes: [
      'BCI&I check required for all direct care applicants per 3701-60-06(A)',
      'FBI check required if not 5-year Ohio resident per 3701-60-06(C)',
      'Renewal check every 5 years per 3701-60-06(B)',
      'Employees hired before 1/1/2008: Check within 30 days of hire anniversary',
      'Employees hired on/after 1/1/2008: Check within 30 days of 5th anniversary',
    ],
  },

  {
    id: 'OH-B3',
    itemId: 'B3',
    stateCode: 'OH',
    title: 'Conditional Employment Compliance',
    description: 'Ohio allows conditional employment pending background check per OAC 3701-60-07',
    severity: 'high',
    regulationCode: '3701-60-07',
    regulationName: 'Conditional Employment',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you ever allow employees to start work before background check results are received?',
        inputType: 'select',
        options: [
          { value: 'never', label: 'No - always wait for results' },
          { value: 'conditional', label: 'Yes - conditional employment per 3701-60-07' },
          { value: 'improper', label: 'Yes - but not following conditional employment rules' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'If using conditional employment, do you meet ALL requirements?',
        inputType: 'checklist',
        options: [
          { value: 'database_clear', label: 'Database review completed with no disqualifying info - 3701-60-07(A)(1)' },
          { value: 'fingerprints', label: 'Fingerprint impressions obtained before start - 3701-60-07(A)(2)' },
          { value: '5_day_request', label: 'Criminal records check requested within 5 business days - 3701-60-07(A)(3)' },
          { value: '60_day_limit', label: 'Terminate if results not received within 60 days - 3701-60-07(D)(1)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'conditional',
        },
        helpText: 'Per 3701-60-07: All conditions must be met for conditional employment.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start without following conditional employment rules (direct violation - must be evaluated FIRST)
      { condition: 'equals', questionId: 'q1', value: 'improper', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if conditional employment used but not meeting all requirements
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'conditional' },
        { condition: 'countLessThan', questionId: 'q2', value: 4 },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT if never allowing early start
      { condition: 'equals', questionId: 'q1', value: 'never', result: 'COMPLIANT' },
      // COMPLIANT if conditional employment with all requirements met
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'conditional' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check timing and conditional employment procedures are compliant.',
      atRisk: 'Conditional employment procedures may be incomplete.',
      nonCompliant: 'VIOLATION: Employees are starting work without following proper conditional employment procedures per OAC 3701-60-07. This is a direct violation - all conditional employment requirements must be met or employees must wait for background check results. Immediately remove non-compliant staff from patient care.',
      unknown: 'Review conditional employment procedures.',
    },
    stateNotes: [
      'Conditional employment allowed per 3701-60-07(A)',
      'Database review must be clear before conditional start',
      'Fingerprints must be obtained before start',
      'Criminal records check request within 5 business days',
      'Must terminate if results not received within 60 days per 3701-60-07(D)(1)',
      'Terminate immediately if disqualifying offense found per 3701-60-07(D)(2)',
    ],
  },

  {
    id: 'OH-B4',
    itemId: 'B4',
    stateCode: 'OH',
    title: 'Disqualifying Offenses and Exclusionary Periods',
    description: 'Ohio has tiered exclusionary periods for disqualifying offenses per OAC 3701-60-09',
    severity: 'high',
    regulationCode: '3701-60-08, 3701-60-09',
    regulationName: 'Disqualifying Offenses',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you screen all background check results against the disqualifying offenses list?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 3701-60-08: Extensive list of disqualifying offenses. 3701-60-09 provides exclusionary period tiers.',
      },
      {
        id: 'q2',
        question: 'Are you aware of the tiered exclusionary periods (Permanent, 10-year, 7-year, 5-year, None)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - understand and apply correctly' },
          { value: 'no', label: 'No - not familiar with tiers' },
        ],
        helpText: 'Per 3701-60-09: Different offenses have different exclusionary periods.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Disqualifying offense screening procedures are in place.',
      atRisk: 'Review exclusionary period tiers in 3701-60-09.',
      nonCompliant: 'Not properly screening for disqualifying offenses.',
      unknown: 'Verify disqualifying offense procedures.',
    },
    stateNotes: [
      'Tier I - Permanent bar: Murder, rape, kidnapping, patient abuse, trafficking, etc.',
      'Tier II - 10-year bar: Involuntary manslaughter, extortion, aggravated arson, etc.',
      'Tier III - 7-year bar: Aggravated assault, stalking, robbery, insurance fraud, etc.',
      'Tier IV - 5-year bar: Assault, menacing, theft, breaking and entering, etc.',
      'Tier V - No bar: Minor drug possession, drug paraphernalia',
      'Multiple offenses may extend exclusionary periods',
      'Certificates of qualification/achievement may allow employment per 3701-60-09(B)',
    ],
  },

  {
    id: 'OH-B5',
    itemId: 'B5',
    stateCode: 'OH',
    title: 'Background Check Records and Roster',
    description: 'Ohio requires maintaining background check records and roster per OAC 3701-60-10',
    severity: 'medium',
    regulationCode: '3701-60-10',
    regulationName: 'Records and Reports',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 466-3543',
    agencyUrl: 'https://odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain required background check documentation?',
        inputType: 'checklist',
        options: [
          { value: 'report', label: 'Criminal records report in confidential personnel file - 3701-60-10(B)(1)' },
          { value: 'database', label: 'Database review disqualifying info documentation - 3701-60-10(B)(2)' },
          { value: 'roster', label: 'Roster of applicants/employees accessible by ODH - 3701-60-10(C)' },
          { value: 'dates', label: 'Roster includes submission and receipt dates - 3701-60-10(C)(2-3)' },
          { value: 'determination', label: 'Roster includes disqualifying offense determination - 3701-60-10(C)(4)' },
        ],
        required: true,
        helpText: 'Per 3701-60-10: Maintain confidential records and accessible roster.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check records and roster meet 3701-60-10 requirements.',
      atRisk: 'Background check documentation may be incomplete.',
      nonCompliant: 'Required background check documentation missing.',
      unknown: 'Review background check documentation requirements.',
    },
    stateNotes: [
      'Criminal records report is NOT public record per 3701-60-10(A)',
      'Maintain sealed within or separate from personnel file per 3701-60-10(B)',
      'Roster must be accessible by ODH Director per 3701-60-10(C)',
      'Roster must include employee name, submission date, receipt date, determination, start date',
      'Must provide written attestation of compliance upon ODH request per 3701-60-10(D)',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL REQUIREMENTS
  // ============================================
  {
    id: 'OH-C1',
    itemId: 'C1',
    stateCode: 'OH',
    title: 'STNA Certification Verification',
    description: 'STNAs must be certified and verified on Ohio Nurse Aide Registry per OAC 3701-17',
    severity: 'high',
    regulationCode: '3701-17, 3701-19',
    regulationName: 'Nurse Aide Registry Requirements',
    agencyName: 'Ohio Department of Health',
    agencyAbbr: 'ODH',
    agencyPhone: '(614) 752-9500',
    agencyUrl: 'https://nurseaideregistry.odh.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL STNAs on the Ohio Nurse Aide Registry before hire?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - 100% verified before hire' },
          { value: 'most', label: 'Most are verified' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'Registry check required per OAC 3701-60-05(A)(6) as part of database review.',
      },
      {
        id: 'q2',
        question: 'Do you verify STNA status is "active" or "in good standing" (not expired or with findings)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Cannot employ if registry shows substantiated abuse, neglect, or misappropriation findings.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'STNA registry verification is complete.',
      atRisk: 'STNA registry verification may be inconsistent.',
      nonCompliant: 'STNAs not being verified on registry before hire.',
      unknown: 'Verify STNA registry check procedures.',
    },
    stateNotes: [
      'STNA certification required from ODH-approved 75-hour program per OAC 3701-19',
      'STNA must be "active" or "in good standing" on registry',
      'Cannot employ if substantiated finding of abuse/neglect/misappropriation',
      'STNA renewal every 24 months',
      'Registry URL: https://nurseaideregistry.odh.ohio.gov',
    ],
  },

  {
    id: 'OH-C2',
    itemId: 'C2',
    stateCode: 'OH',
    title: 'Professional License Verification',
    description: 'All licensed professionals must have current, verified licenses',
    severity: 'high',
    regulationCode: 'ORC Chapter 4723 (Nursing), 4755 (PT/OT)',
    regulationName: 'Professional License Requirements',
    agencyName: 'Ohio Board of Nursing',
    agencyAbbr: 'OBN',
    agencyPhone: '(614) 466-3947',
    agencyUrl: 'https://nursing.ohio.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, OT, SLP, MSW) before hire and at renewal?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all verified before hire and tracked for renewal' },
          { value: 'hire_only', label: 'Verified at hire only' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'Licensed professionals must maintain current licensure.',
      },
      {
        id: 'q2',
        question: 'Do you document license verification in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification is complete.',
      atRisk: 'License verification may not track renewals.',
      nonCompliant: 'Professional licenses not being verified.',
      unknown: 'Verify license verification procedures.',
    },
    stateNotes: [
      'Ohio Board of Nursing: https://nursing.ohio.gov - verify RN/LPN licenses',
      'Ohio Board of PT/OT: https://otptat.ohio.gov - verify therapist licenses',
      'Ohio Board of SLP/Audiology: https://slpaud.ohio.gov - verify speech pathologist licenses',
      'Verify licenses at hire and track renewal dates per ORC Chapter 4723 (Nursing), Chapter 4755 (PT/OT)',
      'Document license verification in personnel files',
    ],
  },

  {
    id: 'OH-C3',
    itemId: 'C3',
    stateCode: 'OH',
    title: 'I-9 Employment Eligibility Verification',
    description: 'Federal I-9 requirements apply to all Ohio employers',
    severity: 'high',
    regulationCode: '8 CFR 274a.2',
    regulationName: 'Employment Eligibility Verification',
    agencyName: 'U.S. Citizenship and Immigration Services',
    agencyAbbr: 'USCIS',
    agencyPhone: '(800) 375-5283',
    agencyUrl: 'https://www.uscis.gov/i-9',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees have properly completed I-9 forms on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have completed I-9s' },
          { value: 'most', label: 'Most employees, some may be incomplete' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        helpText: 'Federal requirement: I-9 must be completed within 3 business days of hire.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'I-9 employment eligibility verification is complete for all employees.',
      atRisk: 'Some I-9 forms may be incomplete. Review all employee files.',
      nonCompliant: 'I-9 compliance issues exist. Federal law requires I-9 for all employees.',
      unknown: 'Verify I-9 completion for all employees.',
    },
    stateNotes: [
      'Complete Section 1 on/before first day of work',
      'Complete Section 2 within 3 business days of hire',
      'Retain for 3 years after hire OR 1 year after termination (whichever is later)',
    ],
  },

  // ============================================
  // SECTION D: TRAINING REQUIREMENTS (Medicare CoP)
  // ============================================
  {
    id: 'OH-D1',
    itemId: 'D1',
    stateCode: 'OH',
    title: 'Home Health Aide Initial Training (Medicare CoP)',
    description: 'Medicare-certified agencies must provide 75 hours initial HHA training per 42 CFR 484.80',
    severity: 'high',
    regulationCode: '42 CFR 484.80(b)',
    regulationName: 'Home Health Aide Training Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare-certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL home health aides complete 75 hours of training before patient contact?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all complete 75 hours from approved program' },
          { value: 'stna', label: 'Yes - through STNA certification (meets requirement)' },
          { value: 'most', label: 'Most complete required training' },
          { value: 'no', label: 'No / not tracking' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 42 CFR 484.80(b): 75 hours including 16 hours supervised practical training.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['all', 'stna'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training meets Medicare CoP requirements.',
      atRisk: 'Some HHAs may not have completed required training.',
      nonCompliant: 'HHAs providing services without required 75-hour training.',
      unknown: 'Verify HHA training records.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      '75 hours total including 16 hours supervised practical training per 484.80(b)',
      'STNA certification (75-hour ODH-approved program) satisfies requirement per 484.80(a)(1)(iii)',
      'Competency evaluation required per 484.80(c)',
      'Training topics specified in 484.80(b)(3)',
    ],
  },

  {
    id: 'OH-D2',
    itemId: 'D2',
    stateCode: 'OH',
    title: 'Annual In-Service Training (Medicare CoP)',
    description: 'Medicare-certified agencies must provide 12 hours annual in-service per 42 CFR 484.80(d)',
    severity: 'medium',
    regulationCode: '42 CFR 484.80(d)',
    regulationName: 'In-Service Training Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides complete 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required training' },
          { value: 'no', label: 'No / not tracking' },
          { value: 'na', label: 'N/A - not Medicare-certified' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.80(d): Minimum 12 hours in-service during each 12-month period.',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['all', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets requirements.',
      atRisk: 'Some staff may be behind on annual training.',
      nonCompliant: 'Annual in-service training not being completed.',
      unknown: 'Verify in-service training records.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      '12 hours per 12-month period per 484.80(d)',
      'May occur while furnishing care to patient',
      'Document training in personnel files',
    ],
  },

  {
    id: 'OH-D3',
    itemId: 'D3',
    stateCode: 'OH',
    title: 'Competency Evaluation',
    description: 'HHAs must demonstrate competency per 42 CFR 484.80(c)',
    severity: 'high',
    regulationCode: '42 CFR 484.80(c)',
    regulationName: 'Competency Evaluation Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides have documented competency evaluations?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all have competency evaluations' },
          { value: 'most', label: 'Most have them' },
          { value: 'no', label: 'No / not tracking' },
          { value: 'na', label: 'N/A - not Medicare-certified' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.80(c): Competency evaluation must include skills demonstration.',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['all', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluations are documented.',
      atRisk: 'Some competency evaluations may be missing.',
      nonCompliant: 'Competency evaluations not being conducted.',
      unknown: 'Verify competency evaluation procedures.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      'Must include observed return demonstration per 484.80(c)(2)',
      'Subject areas listed in 484.80(c)(1)',
      'RN or qualified professional must evaluate',
    ],
  },

  // ============================================
  // SECTION E: SUPERVISION (Medicare CoP)
  // ============================================
  {
    id: 'OH-E1',
    itemId: 'E1',
    stateCode: 'OH',
    title: 'RN Supervision of Home Health Aides (Medicare CoP)',
    description: 'Medicare-certified agencies must provide RN supervision per 42 CFR 484.80(h)',
    severity: 'high',
    regulationCode: '42 CFR 484.80(h)',
    regulationName: 'Supervision Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide RN supervisory assessments of HHA services every 14 days?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - every 14 days for patients receiving skilled services' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - not Medicare-certified or no HHA services' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.80(h)(1): RN supervisory assessment every 14 days for patients receiving skilled services.',
      },
      {
        id: 'q2',
        question: 'For patients NOT receiving skilled services, do you provide RN supervision every 60 days?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - every 60 days' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - all patients receive skilled services' },
        ],
        helpText: 'Per 42 CFR 484.80(h)(2): Every 60 days for patients not receiving skilled services.',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['yes', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision schedule meets Medicare CoP requirements.',
      atRisk: 'RN supervision may not meet required frequency.',
      nonCompliant: 'RN supervision not being provided as required.',
      unknown: 'Verify RN supervision procedures.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      'Patients WITH skilled services: RN assessment every 14 days per 484.80(h)(1)',
      'Patients WITHOUT skilled services: RN assessment every 60 days per 484.80(h)(2)',
      'RN must be familiar with patient and care plan',
      'HHA does not need to be present during assessment per 484.80(h)(1)(i)(B)',
    ],
  },

  // ============================================
  // SECTION F: CLINICAL RECORDS (Medicare CoP)
  // ============================================
  {
    id: 'OH-F1',
    itemId: 'F1',
    stateCode: 'OH',
    title: 'Clinical Record Retention (Medicare CoP)',
    description: 'Medicare-certified agencies must retain records 5 years per 42 CFR 484.110(c)',
    severity: 'high',
    regulationCode: '42 CFR 484.110(c)',
    regulationName: 'Clinical Record Retention',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient clinical records after discharge?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets Medicare CoP)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.110(c)(1): Clinical records retained at least 5 years after discharge.',
      },
      {
        id: 'q2',
        question: 'If you cease operations, do you have a plan to maintain records and notify ODH of storage location?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - plan in place' },
          { value: 'no', label: 'No plan' },
        ],
        helpText: 'Per 42 CFR 484.110(c)(2): Must notify state agency of record storage location.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical record retention meets 5-year Medicare CoP requirement.',
      atRisk: 'Medicare requires 5-year retention. Update your policy.',
      nonCompliant: 'Record retention does not meet Medicare 5-year requirement.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      '5 years from discharge per 42 CFR 484.110(c)(1)',
      'Must notify state agency of storage location if closing per 484.110(c)(2)',
      'Records must comply with HIPAA per 484.110(d)',
      'Patient access: Free copy at next visit or within 4 business days per 484.110(e)',
    ],
  },

  {
    id: 'OH-F2',
    itemId: 'F2',
    stateCode: 'OH',
    title: 'Plan of Care Requirements (Medicare CoP)',
    description: 'Medicare-certified agencies must maintain care plans per 42 CFR 484.60',
    severity: 'high',
    regulationCode: '42 CFR 484.60',
    regulationName: 'Care Planning Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
          { value: 'na', label: 'N/A - not Medicare-certified' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.60: Plan of care must be established and periodically reviewed.',
      },
      {
        id: 'q2',
        question: 'Are care plans reviewed and updated at least every 60 days?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - every 60 days' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A' },
        ],
        helpText: 'Care plans must be reviewed by physician in consultation with agency staff.',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['always', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care procedures meet Medicare CoP requirements.',
      atRisk: 'Plans of care may be created after services begin.',
      nonCompliant: 'Services being provided without written plans of care.',
      unknown: 'Review plan of care process.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      'Established before or during initial evaluation per 484.60(a)',
      'Reviewed by physician in consultation with agency staff',
      'Must include all pertinent diagnoses, patient care instructions, and goals',
      'HHA care plan with written patient care instructions required per 484.80(g)',
    ],
  },

  // ============================================
  // SECTION G: PATIENT RIGHTS
  // ============================================
  {
    id: 'OH-G1',
    itemId: 'G1',
    stateCode: 'OH',
    title: 'Patient Rights (Medicare CoP)',
    description: 'Medicare-certified agencies must inform patients of rights per 42 CFR 484.50',
    severity: 'high',
    regulationCode: '42 CFR 484.50',
    regulationName: 'Patient Rights Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to all patients?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all patients receive written rights' },
          { value: 'most', label: 'Most patients receive them' },
          { value: 'no', label: 'No formal patient rights document' },
          { value: 'na', label: 'N/A - not Medicare-certified' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.50(a): Must inform and educate patient of rights orally and in writing.',
      },
      {
        id: 'q2',
        question: 'Do patient rights include required information about charges and payment sources?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 42 CFR 484.50(c)(7): Must advise of payment expectations and charges.',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['all', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided and documented.',
      atRisk: 'Patient rights distribution may be inconsistent.',
      nonCompliant: 'Patient rights not being provided.',
      unknown: 'Review patient rights procedures.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      'Inform orally AND in writing per 484.50(a)',
      'In language patient can understand per 484.50(a)',
      'Includes confidentiality rights per 484.50(c)(6)',
      'Includes payment information per 484.50(c)(7)',
      'Right to file complaint with state agency per 484.50(c)(4)',
    ],
  },

  // ============================================
  // SECTION H: HEALTH & SAFETY
  // ============================================
  {
    id: 'OH-H1',
    itemId: 'H1',
    stateCode: 'OH',
    title: 'Infection Control Program (Medicare CoP)',
    description: 'Medicare-certified agencies must maintain infection control program per 42 CFR 484.70',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Infection Prevention and Control',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection prevention and control program?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes - comprehensive program' },
          { value: 'partial', label: 'Partial program' },
          { value: 'no', label: 'No program' },
          { value: 'na', label: 'N/A - not Medicare-certified' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.70: Must maintain and document infection prevention and control program.',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['comprehensive', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets requirements.',
      atRisk: 'Infection control program is incomplete.',
      nonCompliant: 'No infection control program exists.',
      unknown: 'Review infection control program requirements.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      'Must follow standard precautions per 484.70(a)',
      'Must educate staff and patients per 484.70(b)',
      'Must investigate infections per 484.70(c)',
      'Must take corrective action per 484.70(d)',
    ],
  },

  {
    id: 'OH-H2',
    itemId: 'H2',
    stateCode: 'OH',
    title: 'Emergency Preparedness (Medicare CoP)',
    description: 'Medicare-certified agencies must have emergency preparedness plan per 42 CFR 484.102',
    severity: 'medium',
    regulationCode: '42 CFR 484.102',
    regulationName: 'Emergency Preparedness Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes - comprehensive written plan' },
          { value: 'partial', label: 'Partial plan' },
          { value: 'no', label: 'No plan' },
          { value: 'na', label: 'N/A - not Medicare-certified' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.102: Must develop and maintain emergency preparedness plan.',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['documented', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is documented.',
      atRisk: 'Emergency preparedness plan is incomplete.',
      nonCompliant: 'No emergency preparedness plan exists.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      'Risk assessment and planning per 484.102(a)',
      'Must include patient evacuation procedures per 484.102(a)(4)',
      'Must track and communicate patient info during emergency',
      'Training and testing required per 484.102(d)',
    ],
  },

  // ============================================
  // SECTION I: QUALITY ASSURANCE
  // ============================================
  {
    id: 'OH-I1',
    itemId: 'I1',
    stateCode: 'OH',
    title: 'Quality Assessment and Performance Improvement (Medicare CoP)',
    description: 'Medicare-certified agencies must have QAPI program per 42 CFR 484.65',
    severity: 'medium',
    regulationCode: '42 CFR 484.65',
    regulationName: 'QAPI Requirements',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented QAPI program?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes - comprehensive QAPI program' },
          { value: 'partial', label: 'Some quality activities, not comprehensive' },
          { value: 'no', label: 'No QAPI program' },
          { value: 'na', label: 'N/A - not Medicare-certified' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.65: Must develop, implement, evaluate, and maintain QAPI program.',
      },
      {
        id: 'q2',
        question: 'Does your QAPI program include performance improvement projects?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'includes',
          value: ['comprehensive', 'partial'],
        },
        helpText: 'Per 42 CFR 484.65(d): Must conduct performance improvement projects.',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['comprehensive', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets Medicare CoP requirements.',
      atRisk: 'QAPI program may be incomplete.',
      nonCompliant: 'No QAPI program exists.',
      unknown: 'Review QAPI requirements.',
    },
    stateNotes: [
      'Applies to Medicare-certified agencies',
      'Data-driven quality assessment per 484.65(a)',
      'Performance improvement projects per 484.65(d)',
      'Must address quality indicators including adverse events',
      'Governing body responsible for QAPI per 484.65(a)',
    ],
  },
];

export default OHIO_QUESTIONS;